import React, { useCallback, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useQuery } from '@tanstack/react-query';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketInsightService from '@app/model/market_insight/service/MarketInsightService';
import { MarketInsightQueryKeys } from '@app/model/market_insight/service/MarketInsightQueryKeys';
import MarketInsightBookingCurve from '@app/model/market_insight/MarketInsightBookingCurve';
import { buildOptions } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightBookingCurveFragment.utilities';
import Portlet from '@app/component/portlet/Portlet';
import MenuButtonFilter, { MenuButtonFilterItem } from '@app/component/menu/MenuButtonFilter';

// The Market Booking Curve (ROO2-131 store, per stay date and nights value).
// Left axis: Market Occupancy in percent (one minus the stored availability, as the Occupancy card).
// Right axis: Market Rate per night.
// The screen never shows a room count (decision 4 of the grill, 2026-09-17).

const NIGHTS_STORAGE_KEY = 'roompulse-market-booking-curve-nights';
const STARS_STORAGE_KEY = 'roompulse-market-booking-curve-stars';
const DEFAULT_NIGHTS = 1;
const DEFAULT_STARS = 0; // 0 = all star classes
const WINDOW_DAYS = 120;

const readStored = (key: string, fallback: number): number => {
	try {
		const raw = window.localStorage.getItem(key);
		const value = raw === null || raw.trim() === '' ? NaN : Number(raw);
		return Number.isSafeInteger(value) && value >= 0 && value <= 2147483647 ? value : fallback;
	} catch {
		return fallback;
	}
};

const store = (key: string, value: number) => {
	try {
		window.localStorage.setItem(key, String(value));
	} catch {
		// a browser can refuse the storage; the choice then lasts for the page only
	}
};

const nightsLabel = (nights: number) => nights === 1 ? '1 Night' : `${nights} Nights`;
const starsLabel = (stars: number) => stars === 0 ? 'All stars' : `${stars} Stars`;


// marketAreaId: the market of the market tab; null = the market of the hotel.
const MarketInsightBookingCurveFragment: React.FunctionComponent<{ statDate: string, marketAreaId: number | null }> = ({ statDate, marketAreaId }) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const [nights, setNights] = useState<number>(() => readStored(NIGHTS_STORAGE_KEY, DEFAULT_NIGHTS));
	const [stars, setStars] = useState<number>(() => readStored(STARS_STORAGE_KEY, DEFAULT_STARS));

	const { data: curve, isLoading, isPlaceholderData, isError } = useQuery({
		queryFn: () => MarketInsightService.findMarketBookingCurve({ companyHotelId: companyHotel.id, statDate: statDate, nights: nights, stars: stars, marketAreaId: marketAreaId }),
		queryKey: MarketInsightQueryKeys.bookingCurve(companyHotel.id, statDate, nights, stars, marketAreaId),
		enabled: companyHotel.settings.main.enableMarketBookingCurve,
		retry: false,
		// Keep menu items mounted while a new selection loads; never reuse the data of another hotel or another market.
		placeholderData: (previous, query) => query?.queryKey[1] === companyHotel.id && query?.queryKey[6] === marketAreaId ? previous : undefined,
	});

	const loading = isLoading || isPlaceholderData;

	const onSelectNights = useCallback((value: number | undefined) => {
		const next = value ?? DEFAULT_NIGHTS;
		setNights(next);
		store(NIGHTS_STORAGE_KEY, next);
	}, []);

	const onSelectStars = useCallback((value: number | undefined) => {
		const next = value ?? DEFAULT_STARS;
		setStars(next);
		store(STARS_STORAGE_KEY, next);
	}, []);

	const nightsItems: MenuButtonFilterItem<number>[] = useMemo(() => {
		const list = (curve?.nights?.length ?? 0) > 0 ? (curve as MarketInsightBookingCurve).nights : [DEFAULT_NIGHTS];
		return list.map(value => ({ name: nightsLabel(value), value: value }));
	}, [curve]);

	const starsItems: MenuButtonFilterItem<number>[] = useMemo(() => {
		const list = (curve?.stars?.length ?? 0) > 0 ? (curve as MarketInsightBookingCurve).stars : [DEFAULT_STARS];
		return list.map(value => ({ name: starsLabel(value), value: value }));
	}, [curve]);

	// The two selects render once in the header (sm and up) and once at the top of the body (below sm),
	// so the phone keeps the title on its own line. Only one of the two is visible at a time.
	const renderSelects = () => <>
		<span className="flex items-center">
			<MenuButtonFilter menuItems={nightsItems}
												selectedValue={curve?.selectedNights ?? DEFAULT_NIGHTS}
												onSelect={onSelectNights}
												menuDescription="Select the length of stay of the market rate."
												disabled={loading || nightsItems.length < 2} />

		</span>
		<span className="flex items-center">
			<MenuButtonFilter menuItems={starsItems}
												selectedValue={curve?.selectedStars ?? DEFAULT_STARS}
												onSelect={onSelectStars}
												menuDescription="Select the star class of the hotels."
												disabled={loading || starsItems.length < 2} />

		</span>
	</>;

	const note = <div className="hidden items-center gap-x-3 whitespace-nowrap sm:flex">
		{renderSelects()}
		<span className="text-xs font-medium text-gray-400">last {WINDOW_DAYS} days</span>
	</div>;

	const mobileSelects = <div className="mb-3 flex items-center gap-x-3 whitespace-nowrap sm:hidden">{renderSelects()}</div>;

	const options = useMemo(() => buildOptions(curve, statDate), [curve, statDate]);
	const hasRate = !!curve && curve.points.some(point => point.marketRate !== null);
	const rateNote = 'Market rate: from the 10th scan day of this stay date';

	return <Portlet title="Market Booking Curve" note={note}>
		{mobileSelects}
		<div className="min-h-booking-curve">
			{loading ? <div className="animate-pulse flex items-center gap-x-2">
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5" />
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5" />
			</div> : isError || !curve || curve.points.length === 0 ? <>
				<p className="block text-xs font-light text-gray-400">Not Available</p>
				{!isError && curve?.rateState === 'TOO_EARLY' && <p className="mt-1 text-xs text-gray-500">The curve starts when the daily market scan reaches this date.</p>}
			</> : <>
				{!hasRate && curve.rateState === 'TOO_EARLY' && <p className="mb-1 text-xs text-gray-400">{rateNote}</p>}
				<HighchartsReact highcharts={Highcharts} options={options} />
			</>}
		</div>
	</Portlet>;
};

export default MarketInsightBookingCurveFragment;
